/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import {
  FITTING_PRIORITIZATION_FACADE_TOKEN,
  IFittingPrioritizationFacade,
} from '../../../../../data-access/src/lib/domain/facades-models/fitting-prioritization-facade-interface';
import { GlobalConstants } from '../../constants/global-constants';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { Observable, of, take } from 'rxjs';
import { SystemInfo } from '../../../../../../toolbelt/data-access/src/lib/domain/system-info';
import { DxoItemDraggingProperties } from './fitting-prioritization-types';
import { FittingPrioritizationRowVm } from '../../models/fitting-prioritization-row-vm';
import { FittingPrioritization } from '../../../../../data-access/src/lib/domain/fitting-prioritization-types';
import { FittingPrioritizationReturnCodes } from '../../models/fitting-prioritization-return-codes-types';
import { DropdownItem } from '../../../../../../shared/ui/src/lib/models/dropdown-item-types';
import { AlertStatus, IAlert } from "../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'fitting-prioritization',
  templateUrl: './fitting-prioritization.component.html',
  styleUrls: ['./fitting-prioritization.component.scss'],
  standalone: false
})
export class FittingPrioritizationComponent implements OnInit {
  @Output() close = new EventEmitter();

  //#region Public members

  public state: SysqueElectricalState;
  public fittingPrioritizationRows: FittingPrioritizationRowVm[] = [];

  // Static values
  public componentTitle = GlobalConstants.FittingPrioritizationTitle;
  public revertChangesButton = GlobalConstants.RevertChangesButton;
  public saveOrderButton = GlobalConstants.SaveOrderButton;
  public fittingTypesPlaceholder = GlobalConstants.FittingTypePlaceholder;
  public columnsNames = GlobalConstants.FittingPrioritizationColumns;
  public shapeKey = GlobalConstants.ShapeKey;

  // The following observables are used to get the data from the backend
  public systems$: Observable<SystemInfo[]>;
  public fittingTypes: string[];

  // The following variables are used to store the selected values
  public selectedSystem: string;
  public selectedShape: string;
  public selectedFittingType: string;

  // The following variables are used to show the alert message
  public alertStatus: AlertStatus;
  public warningMessage = '';
  public showWarning = false;
  public alertHeight = 797;
  public nonAlertHeight = 712;

  // The following variables are used for drag and drop functionality
  public draggedItemIndex: number | null = null;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  //#endregion

  /**
   * Constructs a new instance of the ImportSystemsComponent
   * @param facade - The facade for the import systems
   */
  constructor(@Inject(FITTING_PRIORITIZATION_FACADE_TOKEN) private facade: IFittingPrioritizationFacade) {}

  //#region Public methods

  /**
   * Angular lifecycle method that is called after the component is initialized.
   * This method is used for initialization logic.
   */
  public ngOnInit(): void {
    // Set dockable key to false
    this.facade.updateLocalStorage(GlobalConstants.DockableKey, false);

    // Get the state from the facade
    this.state = this.facade.getState();

    // Initialize the list of systems, shapes, fitting types and items
    this.initializeList();
  }

  //#region Event handlers

  /**
   * Event handler for the system changed event
   * @param event - The event emitted by the system dropdown
   */
  public onSystemChanged(event: DropdownItem): void {
    // If the selected value is undefined, return
    if (!event.value) return;

    // Set the selected system
    this.selectedSystem = event.value;

    // Get shapes of the selected system
    this.facade
      .getMainShape(this.selectedSystem)
      .pipe(take(1))
      .subscribe((shape) => {
        this.selectedShape = shape;

        // Get fitting types of the selected system and shape
        this.facade
          .getFittingTypes(this.selectedSystem, this.selectedShape)
          .pipe(take(1))
          .subscribe((fittingTypes) => {
            this.fittingTypes = fittingTypes;
          });
      });

    // Reset the selected fitting type and fitting prioritization rows
    this.selectedFittingType = '';
    this.fittingPrioritizationRows = [];
  }

  /**
   * Event handler for the fitting type changed event
   * @param event - The event emitted by the fitting type dropdown
   */
  public onFittingTypeChanged(event: DropdownItem): void {
    // If the selected value is undefined, reset values and return
    if (!event.value) {
      // Reset the selected fitting type and fitting prioritization rows
      this.selectedFittingType = '';
      this.fittingPrioritizationRows = [];
      return;
    }

    // Set the selected fitting type
    this.selectedFittingType = event.value;

    // Filter the fittings based on the fitting type
    this.getItemsBasedOnFittyngType();
  }

  //#endregion

  /**
   * Close the window
   */
  public closeWindow(): void {
    this.facade.closeWindow();
    this.close.emit();
  }

  /**
   * This method is used to handle the click event of the dismiss button from the alert message
   */
  public onDismissClick(): void {
    this.facade.resizeWindowHeight(this.nonAlertHeight);
    this.warningMessage = '';
    this.showWarning = false;
  }

  /**
   * When the user starts dragging an item, the index of the dragged item will be stored
   * @param e - The event emitted by the sortable component
   */
  public onDragStart: DxoItemDraggingProperties['onDragStart'] = (e) => {
    this.draggedItemIndex = e.fromIndex;
  };

  /**
   * Reorder the fittings after drag and drop
   * @param e - The event emitted by the sortable component
   */
  public reorderLogic: DxoItemDraggingProperties['onReorder'] = (e) => {
    // If the item is dropped in the same place, do nothing
    if (e.toIndex === this.draggedItemIndex) {
      this.draggedItemIndex = null;
      return;
    }

    // Get the start and end index of the dragged item
    const startIndex = e.fromIndex;
    const endIndex = e.toIndex;

    if (startIndex < endIndex) {
      // Update list of fittings after drag and drop from top to bottom
      this.dragAndDropFromTopToBottom(startIndex, endIndex);
    } else {
      // Update list of fittings after drag and drop from bottom to top
      this.dragAndDropFromBottomToTop(startIndex, endIndex);
    }

    // Reset the dragged item index
    this.draggedItemIndex = null;
  };

  /**
   * Set clicked fitting as selected and unselect the rest of the fittings
   * @param order - The order of the fitting
   */
  public toggleSelected(order: string): void {
    this.fittingPrioritizationRows.forEach((fitting) => {
      if (fitting.order !== order) {
        // If the fitting is not the selected one, it will be unselected
        fitting.selected = false;
      } else {
        // If the fitting is selected, it will be unselected and otherwise
        fitting.selected = !fitting.selected;
      }
    });
  }

  /**
   * Get the styling class for the row
   * @param fittingVm - The row for which the class is needed
   * @returns - The class for the row
   */
  public getRowClass(fittingVm: { order: string; selected: boolean }): string {
    const position = this.fittingPrioritizationRows.findIndex((fitting) => fitting.order === fittingVm.order);

    if (position % 2 !== 0 && fittingVm.selected) {
      return GlobalConstants.UNBANDED_BACKGROUND_SELECTED;
    } else if (position % 2 !== 0 && !fittingVm.selected) {
      return GlobalConstants.UNBANDED_BACKGROUND_UNSELECTED;
    } else if (position % 2 === 0 && fittingVm.selected) {
      return GlobalConstants.BANDED_BACKGROUND_SELECTED;
    } else {
      return GlobalConstants.BANDED_BACKGROUND_UNSELECTED;
    }
  }

  /**
   * Revert the changes made in the order of the fittings
   */
  public revertChanges(): void {
    // Filter the fittings based on the fitting type
    this.getItemsBasedOnFittyngType();
  }

  /**
   * Save the order of the fittings
   */
  public saveOrder(): void {
    // If the user wants to save the order, the list of fittings will be saved
    const items: FittingPrioritization[] = this.getListOfItems();

    // Call the facade to make a request to backend to save the order of the fittings
    this.facade
      .reorderFittings(items, this.selectedSystem, this.selectedShape)
      .pipe(take(1))
      .subscribe((result) => {
        if (result === FittingPrioritizationReturnCodes.Success) {
          // If the reorder was successful, the local storage will be updated
          localStorage.removeItem(GlobalConstants.REORDERED_FITTINGS_SUCCESS);
          this.facade.updateLocalStorage(GlobalConstants.REORDERED_FITTINGS_SUCCESS, 'true');

          // The user will be notified that the changes were saved successfully
          this.showAlert(GlobalConstants.ChangesSaveSuccessfully, AlertStatus.Success);
          
          // All fittings will be unselected
          this.resetSelectedFittings();
        } else {
          // If the reorder was not successful, the user will be notified
          this.showAlert(GlobalConstants.ReorderingFittingsFailed, AlertStatus.Warning);
        }
      });
  }

  //#endregion

  //#region Private methods

  /**
   * Converts the list of fitting prioritization rows to a list of items
   * @returns - The list of items
   */
  private getListOfItems(): FittingPrioritization[] {
    let items: FittingPrioritization[] = [];

    // Get the list of items that will be sent to the backend
    this.fittingPrioritizationRows.forEach((fitting) => {
      items.push({
        name: fitting.label,
        position: fitting.order,
        shape: fitting.shape,
        fittingType: fitting.fittingType,
      });
    });

    return items;
  }

  /**
   * Update the fittingPrioritizationRows array after drag and drop from top to bottom
   * @param startIndex start index of the dragged item
   * @param endIndex end index of the dragged item
   */
  private dragAndDropFromTopToBottom(startIndex: number, endIndex: number): void {
    let selectedItems: FittingPrioritizationRowVm[] = [];
    const allItems = this.fittingPrioritizationRows;

    // Get the selected item
    selectedItems = allItems.slice(startIndex, endIndex + 1);

    // Check if there are no selected items
    if (selectedItems.length === 0) {
      return;
    }

    // Get the first item
    const firstItem = selectedItems.shift();
    const firstOrder = firstItem.order;

    // Update first item with index and order from last item
    firstItem.order = selectedItems[selectedItems.length - 1].order;

    // Update the rest of the items
    const selectedItemsCopy = JSON.parse(JSON.stringify(selectedItems));
    for (let index = 1; index < selectedItems.length; index++) {
      selectedItems[index].order = selectedItemsCopy[index - 1]?.order;
    }

    // Update second item with index and order from first item
    selectedItems[0].order = firstOrder;

    // Add the updated first item to the end of the array
    selectedItems.push(firstItem);

    // Update the fittingPrioritizationRows array
    this.fittingPrioritizationRows.splice(startIndex, endIndex - startIndex + 1, ...selectedItems);

    // Change the position of the fittings based on the order
    this.changePositionOfFittings();
  }

  /**
   * Update the fittingPrioritizationRows array after drag and drop from bottom to top
   * @param startIndex start index of the dragged item
   * @param endIndex end index of the dragged item
   */
  private dragAndDropFromBottomToTop(startIndex: number, endIndex: number): void {
    let selectedItems: FittingPrioritizationRowVm[] = [];
    const allItems = this.fittingPrioritizationRows;

    // Get the selected items
    selectedItems = allItems.slice(endIndex, startIndex + 1);

    // Check if there are no selected items
    if (selectedItems.length === 0) {
      return;
    }

    // Get the last item
    const lastItem = selectedItems.pop();
    const lastOrder = lastItem.order;

    // Update last item with index and order from first item
    lastItem.order = selectedItems[0].order;

    // Update the rest of the items
    const selectedItemsCopy = JSON.parse(JSON.stringify(selectedItems));
    for (let index = 0; index < selectedItems.length - 1; index++) {
      selectedItems[index].order = selectedItemsCopy[index + 1].order;
    }

    // Update last but one item with index and order from last item
    selectedItems[selectedItems.length - 1].order = lastOrder;

    // Add the updated first item to the end of the array
    selectedItems.unshift(lastItem);

    // Update the fittingPrioritizationRows array
    this.fittingPrioritizationRows.splice(endIndex, startIndex - endIndex + 1, ...selectedItems);

    // Change the position of the fittings based on the order
    this.changePositionOfFittings();
  }

  /**
   * Change the position of the fittings based on the order
   */
  private changePositionOfFittings(): void {
    this.fittingPrioritizationRows.forEach((fitting) => {
      fitting.index = fitting.order;
    });
  }

  /**
   * Initialize the list of systems, shapes, fitting types and items
   */
  private initializeList(): void {
    // Get systems and set the selected system
    this.facade
      .getSystems()
      .pipe(take(1))
      .subscribe((systems) => {
        // Set the systems observable
        this.systems$ = of(systems);

        // Set the selected system
        this.selectedSystem = this.state.selectedSystem;

        // If the selected system is undefined, return
        // Selected system is undefined when the user opens the dialog from toolbelt without selecting a system
        if (!this.selectedSystem) {
          this.selectedShape = GlobalConstants.ShapePlaceholder;
          return;
        }

        // Get shapes of the selected system
        this.facade
          .getMainShape(this.selectedSystem)
          .pipe(take(1))
          .subscribe((shape) => {
            this.selectedShape = shape;

            // Get fitting types of the selected system and shape
            this.facade
              .getFittingTypes(this.selectedSystem, this.selectedShape)
              .pipe(take(1))
              .subscribe((fittingTypes) => {
                this.fittingTypes = fittingTypes;
              });
          });
      });
  }

  /**
   * Get the list of items based on fitting type
   */
  private getItemsBasedOnFittyngType(): void {
    this.fittingPrioritizationRows = [];
    let countFittings = 1;

    // Get the fittings based on fitting type
    this.facade
      .getItemsBySystemAndFittingType(this.selectedSystem, this.selectedFittingType)
      .pipe(take(1))
      .subscribe((fittings) => {
        fittings.forEach((item) => {
          if (this.getFittingTypeName(item.fittingType) === this.selectedFittingType) {
            this.fittingPrioritizationRows.push({
              order: countFittings.toString(),
              label: item.name,
              index: item.position,
              shape: item.shape,
              fittingType: item.fittingType,
            });
            countFittings += 1;
          }
        });
      });
  }

  /**
   * Show alert message
   * @param message - The message to be shown in the alert
   * @param alertStatus - The type of the alert
   */
  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.facade.resizeWindowHeight(this.alertHeight);
    this.warningMessage = message;
    this.showWarning = true;
    this.alertStatus = alertStatus;

    // Dismiss the alert message after 5 seconds
    setTimeout(() => {
      this.onDismissClick();
    }, 5000);
  }

  /**
   * Reset the selected fittings to false
   */
  private resetSelectedFittings(): void {
    this.fittingPrioritizationRows = this.fittingPrioritizationRows.map((fitting) => ({ ...fitting, selected: false }));
  }

  /**
   * Get the matching fitting type name
   * @param fittingType - The fitting type
   * @returns - The matching fitting type name
   */
  private getFittingTypeName(fittingType: string): string {
    switch (fittingType) {
      case GlobalConstants.JunctionBoxElbow:
        return GlobalConstants.Elbow;
      case GlobalConstants.JunctionBoxTee:
        return GlobalConstants.Tee;
      case GlobalConstants.JunctionBoxCross:
        return GlobalConstants.Cross;
      default:
        return fittingType;
    }
  }

  //#endregion
}
